<template>
  <form @submit.prevent="submitForm">
    <div class="p-d-flex p-jc-between p-ai-center">
      <h1>{{ calEvent.id ? "Modify " : "New " }} Event</h1>
      <Button
        v-if="action === 'modify'"
        @click="deleteEvent"
        icon="pi pi-trash"
        class="delete-button p-button-rounded p-button-danger"
      />
    </div>

    <div class="p-fluid p-formgrid p-grid">
      <div class="p-field p-col-12">
        <label for="name">Event Name</label>
        <InputText id="name" type="text" v-model="calEvent.title" autofocus />
      </div>

      <div class="p-field-checkbox p-col-6">
        <Checkbox
          id="fullDay"
          v-model="calEvent.fullDay"
          @change="resetTimes"
          :binary="true"
        />
        <label for="fullDay">Full Day Event</label>
      </div>

      <div class="p-field-checkbox p-col-6">
        <Checkbox
          id="range"
          v-model="calEvent.rangeDays"
          @change="resetDates"
          :binary="true"
        />
        <label for="range">Multiple days</label>
      </div>

      <div class="p-field p-col-12">
        <label for="start-date"> {{ dateType.text }} </label>
        <Calendar
          id="start-date"
          v-model="calEvent.dates"
          dateFormat="dd/mm/yy"
          :minDate="minDate"
          :selectionMode="dateType.selection"
          :showIcon="true"
        />
      </div>

      <div class="p-field p-col-6">
        <label for="start-time">Start Time</label>
        <Calendar
          id="start-time"
          v-model="calEvent.startTime"
          :timeOnly="true"
          @date-select="startTimeChanged"
          :disabled="calEvent.fullDay"
        />
      </div>

      <div class="p-field p-col-6">
        <label for="end-time">End Time</label>
        <Calendar
          id="end-time"
          v-model="calEvent.endTime"
          :timeOnly="true"
          @date-select="endTimeChanged"
          :disabled="calEvent.fullDay"
        />
      </div>

      <div class="p-field p-col-12">
        <label for="address">Address</label>
        <InputText id="address" type="text" v-model="calEvent.address" />
      </div>

      <div class="p-field p-col-12">
        <label for="notes">Notes</label>
        <Textarea
          id="notes"
          v-model="calEvent.notes"
          :autoResize="true"
          rows="2"
          cols="30"
        />
      </div>
    </div>
    <Button
      style="width: 100%"
      :label="calEvent.id ? 'Update' : 'Save'"
      type="submit"
      :disabled="!calEvent.dates"
      class="p-mt-1"
    />
  </form>
</template>

<script>
import { ref, computed } from "vue";
import Calendar from "primevue/calendar";
import moment from "moment";
export default {
  emits: ["save-event", "delete-event"],
  components: {
    Calendar
  },
  props: {
    initialDate: {
      required: false,
      type: Date
    },
    action: {
      required: true,
      type: String
    },
    existingEvent: {
      required: false,
      type: Object
    }
  },
  setup(props, context) {
    const calEvent = ref(null);
    const startTimeTouched = ref(false);
    const endTimeTouched = ref(false);
    setCalEvent();

    function setCalEvent() {
      if (props.action === "create") {
        calEvent.value = {
          title: null,
          fullDay: false,
          rangeDays: false,
          dates: props.initialDate ? props.initialDate : null,
          startTime: null,
          endTime: null,
          address: null,
          notes: null
        };
      } else if (props.action === "modify") {
        const existing = props.existingEvent;

        let startTime = null;
        let endTime = null;
        if (existing.time) {
          startTime = setTimes(existing.start);
          endTime = setTimes(existing.end);
        }

        const dates = setDates(existing, endTime);

        calEvent.value = {
          id: existing.id,
          title: existing.title,
          fullDay: existing.fullDay,
          rangeDays: existing.rangeDays,
          dates: dates,
          startTime: startTime,
          endTime: endTime,
          address: existing.address,
          notes: existing.notes
        };
      }
    }

    function setTimes(period) {
      const time = {
        hour: moment(period).get("hour"),
        minute: moment(period).get("minute")
      };

      return moment()
        .set(time)
        .toDate();
    }

    function setDates(existing, endTime) {
      const addDays = endTime ? 0 : -1;
      if (existing.rangeDays) {
        return [
          moment(existing.start).toDate(),
          moment(existing.end)
            .add(addDays, "days")
            .toDate()
        ];
      } else {
        return moment(existing.start).toDate();
      }
    }

    const minDate = ref(new Date());

    const dateType = computed(() => {
      if (calEvent.value.rangeDays) {
        return {
          selection: "range",
          text: "Dates (range)"
        };
      } else {
        return {
          selection: "single",
          text: "Date (single)"
        };
      }
    });

    function resetDates() {
      calEvent.value.dates = null;
    }

    function resetTimes() {
      calEvent.value.startTime = null;
      calEvent.value.endTime = null;
    }

    function submitForm() {
      context.emit("save-event", calEvent.value);
    }

    function startTimeChanged(startTime) {
      startTimeTouched.value = true;
      if (!calEvent.value.endTime || !endTimeTouched.value) {
        calEvent.value.endTime = moment(startTime)
          .add(1, "hours")
          .toDate();
      }
    }

    function endTimeChanged(endTime) {
      endTimeTouched.value = true;
      if (!calEvent.value.startTime || !startTimeTouched.value) {
        calEvent.value.startTime = moment(endTime)
          .add(-1, "hours")
          .toDate();
      }
    }

    function deleteEvent() {
      const confirmDelete = confirm(
        `Are you sure you want to delete ${calEvent.value.title} ? This action is irreversible`
      );

      if (confirmDelete) {
        context.emit("delete-event", calEvent.value.id);
      }
    }

    return {
      calEvent,
      minDate,
      dateType,
      resetDates,
      startTimeChanged,
      endTimeChanged,
      resetTimes,
      deleteEvent,
      submitForm
    };
  }
};
</script>

<style scoped>
.delete-button {
  position: relative;
  top: 3px;
}
</style>
